import type { AiService } from '@/context/services/ai_service_setting/type';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  Caption,
  Heading,
  ListView,
  ListViewBody,
  ListViewLabel,
  ListViewProps,
  Single,
  TextLink,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC } from 'react';
import { ModelTrainingSetting } from '../ModelTrainingSetting/ModelTrainingSetting';
import { AiAgreementLink } from '../aiAgreementLink';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);
const LEFT_RIGHT_RATIO: ListViewProps['leftRightRatio'] = {
  left: {
    max: '264px',
    min: '264px',
  },
  right: {},
};

type AiServiceSettingsProps = {
  isModelTrainingAllowed: boolean;
  isModelTrainingUpdatePending: boolean;
  onModelTrainingAllowedChange: (isAllowed: boolean) => void;
  services: AiService[];
};

export const AiServiceSettings: FC<AiServiceSettingsProps> = ({
  isModelTrainingAllowed,
  isModelTrainingUpdatePending,
  onModelTrainingAllowedChange,
  services,
}) => {
  const { t } = useTranslation();

  return (
    <div className={cx('container')} data-testid='agreed-setting'>
      <Heading as='h2' weight='normal'>
        {t('ai_service_setting.settings_heading')}
      </Heading>

      <section className={cx('services')}>
        {services.map(
          ({
            description,
            detail_url,
            is_enabled,
            is_metered,
            name,
            service_type,
          }) => (
            <article className={cx('service')} key={service_type}>
              <div className={cx('serviceHeader')}>
                <div className={cx('serviceNameRow')}>
                  <Heading as='h3'>{name}</Heading>
                  {is_metered && (
                    <Caption type='info'>
                      {t('ai_service_setting.metered_label')}
                    </Caption>
                  )}
                </div>
                <ButtonV2
                  color='primary'
                  isSecondary
                  isTertiary={is_enabled}
                  size='sm'
                  type='button'
                >
                  {is_enabled
                    ? t('ai_service_setting.service_stop_button')
                    : t('ai_service_setting.service_start_button')}
                </ButtonV2>
              </div>
              <ListView leftRightRatio={LEFT_RIGHT_RATIO}>
                <Single>
                  <ListViewLabel>
                    {t('ai_service_setting.service_setting_label')}
                  </ListViewLabel>
                  <ListViewBody>
                    <p>
                      {is_enabled
                        ? t('ai_service_setting.service_in_use')
                        : t('ai_service_setting.service_not_in_use')}
                    </p>
                    <Caption icon={false} type='helper'>
                      {description}
                      {detail_url && (
                        <TextLink href={detail_url} target='_blank'>
                          {t('ai_service_setting.details_link')}
                        </TextLink>
                      )}
                    </Caption>
                  </ListViewBody>
                </Single>
              </ListView>
            </article>
          )
        )}
      </section>

      <ModelTrainingSetting
        isAllowed={isModelTrainingAllowed}
        isInitial={false}
        isUpdatePending={isModelTrainingUpdatePending}
        onChange={onModelTrainingAllowedChange}
      />

      <AiAgreementLink />
    </div>
  );
};
